import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Outlet, RouterProvider, type RouteObject } from "react-router";

import { OrganizationProvider, RequirePermission } from "@/auth/organization";
import { RedirectIfSignedIn, RequireAuth } from "@/auth/session";
import { AppShell } from "@/components/layout";
import { RouteErrorBoundary } from "@/components/errors";
import { SessionGuard } from "@/components/SessionGuard";
import { LegalTermsBoundary } from "@/components/legal";
import { ToastProvider } from "@/components/ui";
import { SECTION_PERMISSIONS } from "@/lib/access";
import { GROWTH_LIVE } from "@/lib/growth";
import { ApiError } from "@/lib/http";
import { AcceptInvitationPage } from "@/pages/auth/AcceptInvitation";
import { AccountClosedPage, AccountClosureCancelPage } from "@/pages/auth/AccountClosure";
import { TransferOutAnswerPage } from "@/pages/auth/TransferOutAnswer";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPassword";
import { LoginPage } from "@/pages/auth/Login";
import {
  MfaRecoveryCompletePage,
  MfaRecoveryConfirmPage,
  MfaRecoveryStartPage,
} from "@/pages/auth/MfaRecovery";
import { RegisterPage } from "@/pages/auth/Register";
import { ResetPasswordPage } from "@/pages/auth/ResetPassword";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmail";
import { BillingPage } from "@/pages/billing";
import { AddPaymentMethodPage } from "@/pages/billing/AddPaymentMethod";
import { CancelPlanPage } from "@/pages/billing/CancelPlan";
import { CardSavedPage } from "@/pages/billing/CardSaved";
import { CheckoutPage } from "@/pages/billing/Checkout";
import { CheckoutSuccessPage } from "@/pages/billing/CheckoutSuccess";
import { ChoosePlanPage } from "@/pages/billing/ChoosePlan";
import { InvoiceDocumentPage } from "@/pages/billing/InvoiceDocument";
import { AnalyticsPage } from "@/pages/analytics";
import { ActivityPage } from "@/pages/activity";
import { AuditLogPage } from "@/pages/audit";
import { EmailPage } from "@/pages/email";
import { MailboxesPage } from "@/pages/mailboxes";
import { DashboardPage } from "@/pages/dashboard";
import { DomainsPage } from "@/pages/domains";
import { DomainCheckoutPage } from "@/pages/domains/Checkout";
import { DomainContactsPage } from "@/pages/domains/ContactBook";
import { ConnectDomainPage } from "@/pages/domains/Connect";
import { DomainConnectionPage } from "@/pages/domains/Connection";
import { ConnectExternalDomainPage } from "@/pages/domains/ConnectExternal";
import { DnssecPage } from "@/pages/domains/Dnssec";
import { DomainDetailPage } from "@/pages/domains/DomainDetail";
import { FindDomainPage } from "@/pages/domains/FindDomain";
import { DomainOrdersPage } from "@/pages/domains/OrderHistory";
import { SunriseApplyPage } from "@/pages/domains/SunriseApply";
import { DomainOrderPage } from "@/pages/domains/OrderStatus";
import { BulkTransferPage } from "@/pages/domains/BulkTransfer";
import { RouteErrorPage } from "@/pages/error";
import { GrowthComingSoonPage } from "@/pages/growth-intelligence/coming-soon";
import { GrowthIntelligencePage } from "@/pages/growth-intelligence";
import { InsightDetailPage } from "@/pages/growth-intelligence/insight";
import { AllInsightsPage } from "@/pages/growth-intelligence/insights";
import { GrowthOnboardingPage } from "@/pages/growth-intelligence/onboarding";
import { RecommendationsPage } from "@/pages/growth-intelligence/recommendations";
import { SourcesPage } from "@/pages/growth-intelligence/sources";
import { TaskDetailPage } from "@/pages/growth-intelligence/task-detail";
import { HostingPage } from "@/pages/hosting";
import { AddExternalSitePage } from "@/pages/hosting/AddExternalSite";
import { HostedSitePage } from "@/pages/hosting/HostedSite";
import { NotFoundPage } from "@/pages/not-found";
import { NotificationsPage } from "@/pages/notifications";
import { OnboardingPage } from "@/pages/onboarding";
import { PoliciesPage } from "@/pages/policies";
import { PolicyDetailPage } from "@/pages/policies/PolicyDetail";
import { SettingsPage } from "@/pages/settings";
import { SupportPage } from "@/pages/support";
import { TeamPage } from "@/pages/team";
import { WebsitesPage } from "@/pages/websites";
import { WebsiteDetailPage } from "@/pages/websites/WebsiteDetail";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        // Client errors (4xx) will not fix themselves; retry only network / 5xx failures.
        retry: (count, err) =>
          count < 2 && !(err instanceof ApiError && err.status >= 400 && err.status < 500),
      },
      mutations: { retry: false },
    },
  });
}

const growthRoutes: RouteObject[] = [
  { path: "growth-intelligence", element: <GrowthIntelligencePage /> },
  { path: "growth-intelligence/insights", element: <AllInsightsPage /> },
  { path: "growth-intelligence/insights/:insightId", element: <InsightDetailPage /> },
  { path: "growth-intelligence/recommendations", element: <RecommendationsPage /> },
  { path: "growth-intelligence/recommendations/:taskId", element: <TaskDetailPage /> },
  { path: "growth-intelligence/sources", element: <SourcesPage /> },
];

/** Wraps each route of a guarded area (`/domains/...` → `SECTION_PERMISSIONS["/domains"]`). */
export function gated(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    const permission = SECTION_PERMISSIONS[`/${route.path?.split("/")[0] ?? ""}`];
    return permission
      ? { ...route, element: <RequirePermission permission={permission}>{route.element}</RequirePermission> }
      : route;
  });
}

/**
 * Paths Identity's e-mails link to (`/verify-email`, `/reset-password`, `/invitations/accept`,
 * `/mfa-recovery/confirm`, `/mfa-recovery/complete`, `/account-closure/cancel`) and
 * the OAuth return path (`/login`) are part of the contract with authentication-service.
 */
const routesWithoutErrorBoundary: RouteObject[] = [
  {
    path: "/login",
    element: (
      <RedirectIfSignedIn>
        <LoginPage />
      </RedirectIfSignedIn>
    ),
  },
  {
    path: "/register",
    element: (
      <RedirectIfSignedIn>
        <RegisterPage />
      </RedirectIfSignedIn>
    ),
  },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/verify-email", element: <VerifyEmailPage /> },
  // Lost authenticator + recovery codes. The two link pages are where Identity's e-mails point.
  { path: "/mfa-recovery", element: <MfaRecoveryStartPage /> },
  { path: "/mfa-recovery/confirm", element: <MfaRecoveryConfirmPage /> },
  { path: "/mfa-recovery/complete", element: <MfaRecoveryCompletePage /> },
  // Account closure: the page after closing, and the "Keep my account" link Identity's e-mail points to.
  { path: "/account-closure/closed", element: <AccountClosedPage /> },
  { path: "/account-closure/cancel", element: <AccountClosureCancelPage /> },
  // The registrant's e-mailed Approve / Reject link for a transfer away (PHP-3941): no sign-in.
  { path: "/transfer-out/:token", element: <TransferOutAnswerPage /> },
  {
    path: "/invitations/accept",
    element: (
      <RequireAuth>
        <AcceptInvitationPage />
      </RequireAuth>
    ),
  },
  {
    path: "/onboarding",
    element: (
      <RequireAuth>
        <OnboardingPage />
      </RequireAuth>
    ),
  },
  {
    element: (
      <RequireAuth>
        <LegalTermsBoundary>
          <OrganizationProvider>
            <Outlet />
          </OrganizationProvider>
        </LegalTermsBoundary>
      </RequireAuth>
    ),
    children: [
      // printable, without the portal chrome
      { path: "billing/invoices/:invoiceId", element: <InvoiceDocumentPage /> },
      // the Growth Intelligence set-up is a full-screen flow of its own
      ...(GROWTH_LIVE
        ? [{ path: "growth-intelligence/setup/:step?", element: <GrowthOnboardingPage /> }]
        : []),
      {
        element: <AppShell />,
        // inside the organization, so the crash page can still open the help panel
        errorElement: <RouteErrorPage withHelp />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "notifications", element: <NotificationsPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "policies", element: <PoliciesPage /> },
          { path: "policies/:versionId", element: <PolicyDetailPage /> },
          { path: "support", element: <SupportPage /> },
          // each area only for people who may see it (the same rule hides its sidebar item)
          ...gated([
            // hidden until launch: every /growth-intelligence path (set-up included) lands on "coming soon"
            ...(GROWTH_LIVE
              ? growthRoutes
              : [{ path: "growth-intelligence/*", element: <GrowthComingSoonPage /> }]),
            { path: "websites", element: <WebsitesPage /> },
            { path: "websites/:siteId", element: <WebsiteDetailPage /> },
            { path: "email", element: <EmailPage /> },
            { path: "email/mailboxes", element: <MailboxesPage /> },
            { path: "domains", element: <DomainsPage /> },
            { path: "domains/find", element: <FindDomainPage /> },
            { path: "domains/transfer/bulk", element: <BulkTransferPage /> },
            { path: "domains/checkout", element: <DomainCheckoutPage /> },
            { path: "domains/orders", element: <DomainOrdersPage /> },
            { path: "domains/sunrise/apply", element: <SunriseApplyPage /> },
            { path: "domains/contacts", element: <DomainContactsPage /> },
            { path: "domains/orders/:orderId", element: <DomainOrderPage /> },
            { path: "domains/connect-external", element: <ConnectExternalDomainPage /> },
            { path: "domains/:domainId", element: <DomainDetailPage /> },
            { path: "domains/:domainId/connect", element: <ConnectDomainPage /> },
            { path: "domains/:domainId/connection", element: <DomainConnectionPage /> },
            { path: "domains/:domainId/dnssec", element: <DnssecPage /> },
            { path: "hosting", element: <HostingPage /> },
            { path: "hosting/external/new", element: <AddExternalSitePage /> },
            { path: "hosting/sites/:siteId", element: <HostedSitePage /> },
            { path: "analytics", element: <AnalyticsPage /> },
            { path: "team", element: <TeamPage /> },
            { path: "audit", element: <AuditLogPage /> },
            { path: "activity", element: <ActivityPage /> },
            { path: "billing", element: <BillingPage /> },
            { path: "billing/plans", element: <ChoosePlanPage /> },
            { path: "billing/checkout", element: <CheckoutPage /> },
            { path: "billing/checkout/success/:invoiceId", element: <CheckoutSuccessPage /> },
            { path: "billing/payment-methods/new", element: <AddPaymentMethodPage /> },
            { path: "billing/payment-methods/:methodId/saved", element: <CardSavedPage /> },
            { path: "billing/cancel", element: <CancelPlanPage /> },
          ]),
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

/**
 * A render, loader or action error anywhere under a top-level route bubbles up to that route's own
 * `errorElement` (routes below have none of their own), so the crash screen replaces only the page
 * that broke — the rest of the router, and a reload, still work.
 */
export const routes: RouteObject[] = routesWithoutErrorBoundary.map((route) => ({
  ...route,
  errorElement: <RouteErrorBoundary />,
}));

const router = createBrowserRouter(routes);
const queryClient = createQueryClient();

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
        {/* beside the router, never inside a route: nothing remounts when the session ends */}
        <SessionGuard />
      </ToastProvider>
    </QueryClientProvider>
  );
}
